import * as persistenceKeys from '@web-platform/shared-universal-constants/src/persistanceKeys';
import { MULTI_DESTINATION } from '@web-platform/shared-universal-constants/src/tripTypes';
import { observeStore } from '@web-platform/shared-universal-redux/src/helpers/observeStore';
import { getUserSearch } from '@web-platform/shared-universal-redux/src/state';
import {
  isAcornRequest,
  isAcornType,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import { trySetValue } from '@web-platform/shared-universal-utils-frontend/src/helpers/sessionStorage';

import type {
  AcornState,
  BananaState,
  StateType,
  StoreType,
} from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';

interface WatchedKeyConfig<TState> {
  selector: (state: TState) => unknown;
}

type WatchedKeysReturn =
  Record<string, WatchedKeyConfig<AcornState>> | Record<string, WatchedKeyConfig<BananaState>>;

async function getWatchedKeys(): Promise<WatchedKeysReturn> {
  if (isAcornRequest()) {
    const { getMixedAirportFilter: getAcornMixedAirportFilter } = await import(
      /* webpackChunkName: "FlightsDayView-State" */
      '@web-platform/shared-acorn-redux/src/state/flightsDayView'
    );
    const { getAirportsFilter: getAcornAirportsFilter } = await import(
      /* webpackChunkName: "Acorn-State" */
      '@web-platform/shared-acorn-redux/src/state'
    );
    return {
      [persistenceKeys.FILTERS]: {
        selector: (state: AcornState) => ({
          airports: getAcornAirportsFilter(state),
          mixedAirport: getAcornMixedAirportFilter(state),
        }),
      },
    };
  }
  const {
    getAirportsFilter,
    getAlliancesFilter,
    getCarriersFilter,
    getDepartureTimesFilter,
    getDurationFilter,
    getEcoFilter,
    getMixedAirportFilter,
    getStopsFilter,
  } = await import(
    /* webpackChunkName: "FlightSearchResults-State" */
    '@web-platform/shared-universal-redux/src/state/shared/flightSearchResults'
  );
  return {
    [persistenceKeys.FILTERS]: {
      selector: (state: BananaState) => {
        const { flightSearchResults } = state.shared;

        return {
          stops: getStopsFilter(flightSearchResults),
          airports: getAirportsFilter(flightSearchResults),
          alliances: getAlliancesFilter(flightSearchResults),
          mixedAirport: getMixedAirportFilter(flightSearchResults),
          carriers: getCarriersFilter(flightSearchResults),
          duration: getDurationFilter(flightSearchResults),
          departureTimes: getDepartureTimesFilter(flightSearchResults),
          eco: getEcoFilter(flightSearchResults),
        };
      },
    },
  };
}

const actUponState = (key: string) => (getState: () => unknown) => {
  trySetValue(key, getState());
};

function getIsMultiDestination(state: StateType) {
  if (isAcornType<AcornState, BananaState>(state)) {
    return getUserSearch(state).tripType === MULTI_DESTINATION;
  }
  return false;
}

export async function observeSessionStorageState(store: StoreType): Promise<() => void> {
  const watchedKeys = await getWatchedKeys();

  const shouldSkipObservation = getIsMultiDestination(store.getState());

  const unsubscribeMethods = shouldSkipObservation
    ? []
    : Object.keys(watchedKeys).map((key) => {
        const config = watchedKeys[key];
        // @ts-expect-error TS2345: Argument of type 'State' is not assignable to parameter of type 'State'.
        return observeStore(store, config.selector, actUponState(key));
      });

  return () => {
    for (const unsubscribe of unsubscribeMethods) {
      unsubscribe();
    }
  };
}
